import { DialogActions, DialogContent, Skeleton, Stack } from '@mui/material'
import { CoreTypography, LeafDialog } from '@thriveglobal/thrive-web-leafkit'
import React, { memo, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import ConditionalSkeleton from '../../../../../components/elements/ConditionalSkeleton/ConditionalSkeleton'
import { useGetCheckboxPolicyQuery } from '../../../../../graphql/generated/autogenerated'
import SaveToSAcceptance from '../SaveToSAcceptance/SaveToSAcceptance'
import ToSAcceptanceFormController, {
    DEFAULT_POLICY_STATUS
} from '../ToSAcceptance/ToSAcceptanceFormController'
const messages = defineMessages({
    tosAcceptanceDialogTitle: {
        defaultMessage: 'Our privacy policy and terms of service',
        description: 'Our privacy policy and terms of service'
    }
})

export interface ToSAcceptanceModalProps extends JSX.IntrinsicAttributes {
    open: boolean
    onAcceptToS: (success: boolean) => Promise<void>
}

export const ToSAcceptanceModal: React.FC<ToSAcceptanceModalProps> = ({
    open,
    onAcceptToS
}) => {
    const { formatMessage } = useIntl()

    const { loading, data } = useGetCheckboxPolicyQuery()
    const formMethods = useForm({
        defaultValues: DEFAULT_POLICY_STATUS
    })
    const { handleSubmit } = formMethods

    const { country, companyName, additionalCheckboxPolicies } = useMemo(
        () => ({
            country: data?.identity?.user?.getCountryCodeHeader,
            companyName: data?.identity?.me?.company?.name,
            additionalCheckboxPolicies:
                data?.identity?.company?.getCompanyTermsOfService
        }),
        [data]
    )

    return (
        <LeafDialog
            fullWidth
            open={open}
            hideCloseButton
            disableEscapeKeyDown
            dialogTitle={formatMessage(messages.tosAcceptanceDialogTitle)}
            sx={{
                zIndex: 9999
            }}
        >
            <DialogContent>
                {loading ? (
                    <Skeleton variant="rectangular" height={200} />
                ) : (
                    <Stack>
                        <FormProvider {...formMethods}>
                            <ToSAcceptanceFormController
                                country={country}
                                companyName={companyName}
                                additionalCheckboxPolicies={
                                    additionalCheckboxPolicies
                                }
                            />
                        </FormProvider>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <ConditionalSkeleton showSkeleton={loading}>
                    <SaveToSAcceptance
                        color="primary"
                        disabled={loading}
                        onSavedToSAcceptance={onAcceptToS}
                        onHandleFormSubmit={(acceptTos) =>
                            handleSubmit(acceptTos)
                        }
                        size="large"
                        variant="contained"
                    >
                        <CoreTypography customVariant="buttonLarge">
                            <FormattedMessage
                                defaultMessage="Agree and get started"
                                description="Accept the privacy policy and terms of service"
                            />
                        </CoreTypography>
                    </SaveToSAcceptance>
                </ConditionalSkeleton>
            </DialogActions>
        </LeafDialog>
    )
}

export default memo(ToSAcceptanceModal)
